/**
 * @file Header.jsx
 * @description Kopfzeilen-Komponente der App.
 *
 * Was der Header anzeigt:
 * - Den dynamischen Titel der aktuell geöffneten Ansicht (z. B. "Dashboard", "Daily Todos", "Thekipedia").
 * - Für angemeldete Benutzer auf Smartphones und Tablets: Einen Hamburger-Menü-Button (☰),
 *   um die seitliche Navigationsleiste (`Sidebar`) aufzuklappen.
 */

import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, Settings } from 'lucide-react';
import Sidebar from './Sidebar';
import './Header.css';

/**
 * Kopfzeile mit Seitentitel und Schalter für das mobile Menü.
 *
 * @param {object} props - Komponenten-Properties
 * @param {string} props.title - Titel der aktuellen Seite
 * @returns {JSX.Element} Kopfzeilen-Element
 */
export default function Header({ title }) {
  const { user, role } = useAuth();
  // Steuert, ob das mobile Ausklapp-Menü (Drawer) gerade offen ist
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const canManageSettings = role === 'System-Admin' || role === 'Admin';

  return (
    <>
      <header className="header">
        <div className="header-left">
          {/* Menü-Button nur für eingeloggte Benutzer anzeigen */}
          {user && (
            <button 
              className="menu-button" 
              onClick={() => setSidebarOpen(true)}
              aria-label="Menü öffnen"
            >
              <Menu size={24} />
            </button>
          )}
          <h1 className="header-title">{title}</h1>
        </div>

        {/* Zahnrad oben rechts für Admin & System-Admin */}
        {canManageSettings && (
          <div className="header-right">
            <NavLink
              to="/admin/settings"
              className={({ isActive }) => `header-settings-btn ${isActive ? 'active' : ''}`}
              title="System-Einstellungen (Schichtzeiten)"
              aria-label="System-Einstellungen"
            >
              <Settings size={20} />
            </NavLink>
          </div>
        )}
      </header>

      {/* Die einblendbare Seitenleiste für die Navigation */}
      {user && (
        <Sidebar 
          isOpen={sidebarOpen} 
          onClose={() => setSidebarOpen(false)} 
        />
      )}
    </>
  );
}
